import axios from 'axios';
import React, { act, createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import {JAVA_BACKEND_API_URL, NODE_BACKEND_API_URL, NODE_BACKEND_API_PORT, JAVA_BACKEND_USER_PASS} from "../BackendConstants";
import MainContext from "../MainContext";
import { useTranslation } from "react-i18next";

const BetterStandsContext = createContext();

export const BetterStandsProvider = ({ children }) => {
  const { t } = useTranslation("components");
    // Backlog #44: this used to be its own independently-fetched state, so switching the active
    // fair in the Header (MainContext.selectedFuarId) never reached this context — one session
    // could show two different "active" fairs at once. MainContext.selectedFuarId is now the one
    // source of truth; this stays in sync with it below instead of drifting on its own.
    const { selectedFuarId } = useContext(MainContext);
    const [stands, setStands] = useState([]);
    const [salons, setSalons] = useState([]);
    const [uncategorizedPhotos, setUncategorizedPhotos] = useState([]);
    const [detailedStands, setDetailedStands] = useState({});
    const [isLoadingDetailed, setIsLoadingDetailed] = useState(false);
    const [detailedError, setDetailedError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [filteredPhotos, setFilteredPhotos] = useState([]);
    const [fetchedFairId, setFetchedFairId] = useState(null);
    // Backlog #44: activeFairId follows MainContext.selectedFuarId — the one source of truth — so the
    // Header's fuar switcher reaches this context immediately, instead of it only ever updating from
    // fetchActiveFairId(userId) below. That call hits the exact same backend endpoint, so fetchedFairId
    // simply agrees with it and serves as the fallback until the Header has set one. Derived at render
    // rather than mirrored into state in an effect, which would cost an extra render pass per switch
    // — and it has to be declared here, above the useCallback dep arrays that read it.
    const activeFairId = selectedFuarId != null ? selectedFuarId : fetchedFairId;
    const [authToken, setAuthToken] = useState(localStorage.getItem("token"));
    const [fuarZamani, setFuarZamani] = useState(null);
    const [categories] = useState([
        { id: 11, key: "ana_fotograf", tr: "Ana Fotoğraf", en: "Main Photo" },
        { id: 1, key: "stant_yapisi_ve_duvarlar", tr: "Stand Yapısı ve Duvarlar", en: "Stand Structure and Walls" },
        { id: 2, key: "platform", tr: "Platform", en: "Platform" },
        { id: 3, key: "mobilya_ve_techizat", tr: "Mobilya ve Teçhizat", en: "Furniture and Equipment" },
        { id: 4, key: "aydinlatma", tr: "Aydınlatma", en: "Lighting" },
        { id: 5, key: "alinlik_ve_bas_ustu", tr: "Alınlık ve Baş Üstü", en: "Fascia and Overhead Signage" },
        { id: 7, key: "tavan", tr: "Tavan", en: "Ceiling" },
        { id: 8, key: "teshir_bilesenleri", tr: "Teşhir Bileşenleri", en: "Display facilities" },
        { id: 9, key: "zemin", tr: "Zemin", en: "Floor" },
        { id: 10, key: "grafikler_dekoratifler", tr: "Grafikler ve Dekoratifler", en: "Graphics and Decorations" },
    ]);

    const sahaapiUrl = useMemo(() => `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/sahaform/`, []);

   // const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS),[];
    const authHeader = useMemo(() => 'Basic ' + btoa(JAVA_BACKEND_USER_PASS), []);

    useEffect(() => {
        async function syncToken() {
            const storedToken = localStorage.getItem("token");
            if (storedToken !== authToken) {
                setAuthToken(storedToken);
            }
        }
        syncToken();
    }, [authToken]);

    const getPostHeader = {
        Authorization: authHeader,
        authToken: authToken,
        userId: localStorage.getItem("userId"),
    };

    const saveCategoryNote = async (noteData) => {
        try {
            const response = await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/save-category-note`, noteData);
            return response.data;
        } catch (error) {
            console.error("Kategori notu kaydedilirken hata oluştu:", error);
            throw error;
        }
    };

    const fetchCategoryNotes = async (standId) => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/get-category-notes`, {
                params: { stand_id: standId },
            });
            console.log('Gelen yanıt:', response.data); // Gelen veriyi kontrol edin
            return response.data;
        } catch (error) {
            console.error("Kategori notları alınırken hata oluştu:", error);
            return [];
        }
    };

    const fetchCategoryPhotos = useCallback(async (standId) => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/photos-by-stand`, {
                params: { stand_id: standId },
            });

            return response.data; // Fotoğraf listesi
        } catch (error) {
            console.error("Stant fotoğrafları alınırken hata oluştu:", error);
            return [];
        }
    }, []);
    // map backend codes ↔︎ UI labels
    const STAGE_MAP = useMemo(() => ({
        1: "fuar_oncesi",
        2: "fuar_sonrasi",
    }), []);

    const STAGE_CODE = useMemo(() => ({
        fuar_oncesi: 1,
        fuar_sonrasi: 2,
    }), []);

    const updateFairStage = useCallback(
        async (stageValue) => {
            if (!activeFairId) return;

            // keep existing UI state in the familiar string form
            setFuarZamani(stageValue);

            try {
                await axios.post(
                    "https://e.sakagrup.com:5000/api/betterstands/fairs/set-stage",
                    {
                        fuarId: activeFairId,        // <-- camelCase key
                        stage: STAGE_CODE[stageValue]
                    },
                    { headers: { "Content-Type": "application/json" } }
                );
            } catch (error) {
                console.error("Fair stage update failed:", error);
                // optional rollback:
                // setFuarZamani(prev => prev);
                throw error;
            }
        },
        [activeFairId, STAGE_CODE]
    );

    const fetchActiveFairId = useCallback(async (userId) => {
        try {
            const response = await axios.get(
                `${JAVA_BACKEND_API_URL}/fuar/activefuarbyuserid/${userId}`,
                {
                    params: { userId },
                    headers: {
                        Authorization: authHeader,
                        "X-token": authToken,
                        "X-user-id": localStorage.getItem("userId"),
                    },
                }
            );

            console.log("Response data:", response.data);

            // API'den gelen response'da id alanı var
            const fairId = response.data?.id;
            const fairTime = response.data?.fairStartDate; // fairStartDate kullanıyoruz

            if (fairId) {
                setFetchedFairId(fairId);
                // fairTime kontrolü gerekirse burada yapın
                if (fairTime) {
                    // Tarihten aşama belirlemek için logic ekleyin
                    setFuarZamani("fuar_oncesi"); // veya dinamik olarak belirleyin
                }
            } else {
                console.error("Fuar ID bilgisi alınamadı. Response:", response.data);
            }
        } catch (error) {
            console.error("Aktif fuar bilgisi alınırken hata oluştu:", error);
        }
    }, [authHeader, authToken, STAGE_MAP]);

    const fetchSalons = useCallback(async () => {
        console.log('fetchSalons called for fairId:', activeFairId);
        if (!activeFairId) {
            console.error("Aktif fuar ID'si bulunamadı. Salonlar getirilemiyor.");
            return;
        }

        try {
            const response = await axios.get(
                `${JAVA_BACKEND_API_URL}/betterStands/salonsByFuarId/${activeFairId}`,
                {
                    headers: {
                        Authorization: authHeader,
                        "X-token": authToken,
                        "X-user-id": localStorage.getItem("userId"),
                    },
                }
            );

            if (response.data && response.data.salons) {
                const salonsData = response.data.salons.map((salon) => ({
                    salon_id: salon.salon_id,
                    salon_name: salon.salon_name,
                }));

                setSalons(salonsData);
                console.log("Salonlar başarıyla yüklendi:", salonsData);
            } else {
                console.error("Yanıt içinde salon bilgisi bulunamadı:", response.data);
                setSalons([]);
            }
        } catch (error) {
            console.error("Salonlar alınırken hata oluştu:", error);
        }
    }, [activeFairId, authToken, authHeader]);

    const formStatusCheckFn = async (fuarId) => {
        try {
            const response = await axios.get(
                `${JAVA_BACKEND_API_URL}/form/getFormAnswers`,
                {
                    params: { fuarId },
                    headers: {
                        Authorization: authHeader,
                        'X-token': authToken,
                        'X-user-id': localStorage.getItem('userId'),
                    }
                }
            );
            return response.data || {};
        } catch (error) {
            console.error('Rapor alınırken hata oluştu:', error);
            return {};
        }
    };

    // formStatusCheck - inline define to avoid hoisting issues
   // const formStatusCheckFn = async (fuarId) => {
      //  try {
           // const response = await axios.get(sahaapiUrl+"getFormAnswers?fuarId="+fuarId);
            //return response.data || {};
        //} catch (error) {
            //console.error('Rapor alınırken hata oluştu:', error);
            //return {};
        //}
    //};
    // Add this useEffect right after the fetchSalons function
    useEffect(() => {
        if (activeFairId) {
            async function run() { await fetchSalons(); }
            run();
        }
    }, [activeFairId, fetchSalons]);

    const assignPhotoToStand = async (photoId, standId, categoryId) => {
        try {
            await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/assign-photo-to-stand`, {
                photoId,
                standId,
                categoryId,
            });
            console.log("Fotoğraf başarıyla eşlendi.");
        } catch (error) {
            console.error("Fotoğraf eşlenirken hata oluştu:", error);
        }
    };

    const fetchStands = async (userId) => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/uncategorized-photos`, {
                params: { userId },
            });

            if (response.data && response.data.data) {
                setUncategorizedPhotos(response.data.data); // Eşlenmemiş fotoğrafları güncelle
            } else {
                console.warn("Eşlenmemiş fotoğraf bulunamadı, boş bir liste ayarlandı.");
                setUncategorizedPhotos([]);
            }
        } catch (error) {
            console.error("Eşlenmemiş fotoğraflar alınırken hata oluştu:", error);
            setUncategorizedPhotos([]);
        }
    };

    const fetchActiveStands = useCallback(
        async (page = 1, limit = 10, salonFilter = null, searchQuery = '') => {
            if (!authToken) {
                console.error("authToken eksik, API çağrısı yapılmadı.");
                return;
            }

            setLoading(true);

            if (activeFairId) {
                try {
                    // Signal oluştur
                    const controller = new AbortController();
                    const abortSignal = controller.signal;

                    // Timeout için timer ekle
                    const timeoutId = setTimeout(() => controller.abort(), 30000);

                    try {
                        const response = await axios.get(
                            `${JAVA_BACKEND_API_URL}/betterStands/standsByFuarIdOpt2/${activeFairId}`,
                            {
                                params: {
                                    page,
                                    limit,
                                    salonId: salonFilter,
                                    search: searchQuery,
                                    _t: new Date().getTime(), // cache önlemek için
                                },
                                headers: {
                                    'Authorization': authHeader,
                                    'X-token': authToken,
                                    'X-user-id': localStorage.getItem("userId"),
                                    'Cache-Control': 'no-cache',
                                    'Pragma': 'no-cache',
                                    'Expires': '0'
                                },
                                signal: abortSignal
                            }
                        );

                        // Timeout'ı temizle
                        clearTimeout(timeoutId);

                        console.log("📦 API request params:", { page, limit, salonFilter, searchQuery });

                        const formStatusResponse = await formStatusCheckFn(activeFairId);
                        const formStatusMap = {};
                        
                        if (formStatusResponse && Array.isArray(formStatusResponse)) {
                            formStatusResponse.forEach(item => {
                                const standId = item.standId;
                                const formTypeId = item.formTypeId;
                                const formContents = item.sahaformcontentsGrouped;
                                const answers = {};
                                
                                if (Array.isArray(formContents)) {
                                    formContents.forEach(contentItem => {
                                        answers[contentItem.answerValue] = contentItem.count;
                                    });
                                }

                                if (!formStatusMap[standId]) {
                                    formStatusMap[standId] = {};
                                }

                                formStatusMap[standId][formTypeId] = { 
                                    answers: answers, 
                                    status: item.isDone 
                                };
                            });
                        }

                        const standsData = response.data?.fullStandInfoDTOS?.content?.map((stand) => ({
                            stand_id: stand.standId,
                            stand_name: stand.standNumber,
                            hall: stand.hallName,
                            name: stand.companyName,
                            type: stand.standType,
                            firmaId: stand.companyId,
                            formStatus: formStatusMap[stand.standId] ? formStatusMap[stand.standId] : {},
                        })) || [];

                        setStands(standsData);
                        console.log("Updated stands state with", standsData.length, "stands for page", page);
                        setTotalPages(response.data?.fullStandInfoDTOS?.totalPages || 1);
                    } finally {
                        clearTimeout(timeoutId);
                    }
                } catch (error) {
                    if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
                        console.log('İstek iptal edildi veya timeout oluştu:', error.message);
                    } else if (error.response) {
                        console.error('Sunucu hatası:', {
                            status: error.response.status,
                            statusText: error.response.statusText,
                            data: error.response.data
                        });
                    } else if (error.request) {
                        console.error('Sunucuya ulaşılamadı:', error.message);
                    } else {
                        console.error('İstek gönderilirken hata oluştu:', error.message);
                    }

                    // Hata durumunda boş liste göster
                    setStands([]);
                    setTotalPages(1);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        },
        [activeFairId, authToken, authHeader]
    );
    // authToken'ı bağımlılık olarak ekleyin

    const fetchDetailedReport = useCallback(async (fairId) => {
        if (!fairId) return;

        setIsLoadingDetailed(true);
        setDetailedError(null);

        try {
            const response = await axios.get(
                `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/detailed-report-opt2/${fairId}`,
                {
                    headers: {
                        'x-user-id': localStorage.getItem('userId') || '3172', // Fallback to hardcoded if needed
                        'x-token': authToken || "Fhsa6wJeZfA=" // Fallback to hardcoded if needed
                    }
                }
            );

            //console.log("🔍 RAW RESPONSE:", response);
            //console.log("📦 DATA FIELD:", response.data);

            // Use the data directly without transformation
            if (Array.isArray(response.data)) {
                const standsMap = {};
                response.data.forEach(stand => {
                    if (stand.standId) {
                        standsMap[stand.standId] = stand;
                    }
                });
                setDetailedStands(standsMap);
                return standsMap;
            } else {
                console.warn("Expected array but got:", response.data);
                setDetailedStands({});
                return {};
            }
        } catch (error) {
            console.error("Error fetching detailed report:", error);
            setDetailedError(error.message || "Failed to fetch detailed report");
            throw error;
        } finally {
            setIsLoadingDetailed(false);
        }
    }, [authToken]);


    const saveReport = useCallback(async (reportData) => {
        try {
            const response = await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/report`, reportData);
            console.log(response.data.message);
        } catch (error) {
            console.error('Rapor kaydedilirken hata oluştu:', error);
        }
    }, []);

    const fetchReport = useCallback(async (standId) => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/report`, {
                params: { stand_id: standId, fuar_id: activeFairId }
            });

            const reportData = response.data || {};
            return reportData;
        } catch (error) {
            console.error('Rapor alınırken hata oluştu:', error);
            return {};
        }
    }, [activeFairId]);

    const uploadPhoto = async (formData) => {
        try {
            const response = await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/upload-photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log("Fotoğraf Yükleme Yanıtı:", response.data); // Yanıtın doğru olup olmadığını kontrol edin
            return response;
        } catch (error) {
            console.error("Fotoğraf yüklenirken hata oluştu:", error);
            throw new Error(t("betterStandsContext.fotografYuklenirkenBirHataOlustu"));
        }
    };

    const fetchUncategorizedPhotos = useCallback(async (userId) => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/uncategorized-photos`, {
                params: { userId }
            });

            if (response.data && response.data.data) {
                setUncategorizedPhotos(response.data.data);
                setFilteredPhotos(response.data.data); // Filtreleme için başlangıç değeri
            }
        } catch (error) {
            console.error("Eşlenmemiş görseller alınırken hata oluştu:", error);
            setUncategorizedPhotos([]);
        }
    }, []);

    const fetchAllUncategorizedPhotos = useCallback(async () => {
        try {
            const response = await axios.get(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/all-uncategorized-photos`);
            if (response.data && response.data.data) {
                setUncategorizedPhotos(response.data.data);
                setFilteredPhotos(response.data.data);
            }
        } catch (error) {
            console.error("Tüm eşlenmemiş görseller alınırken hata oluştu:", error);
            setUncategorizedPhotos([]);
        }
    }, []);


    const uploadAreaPhoto = async (formData) => {
        try {
            const response = await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/upload-areaphoto`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log("Fotoğraf Yükleme Yanıtı:", response.data); // Yanıtın doğru olup olmadığını kontrol edin
            return response;
        } catch (error) {
            console.error("Fotoğraf yüklenirken hata oluştu:", error);
            throw new Error(t("betterStandsContext.fotografYuklenirkenBirHataOlustu"));
        }
    };

    const fetchPhotosByFuar = useCallback(async (fuarId, salonId = null) => {
        try {
            let url = `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/photos-by-fuar/${fuarId}`;
            if (salonId) {
                url += `?salon_id=${salonId}`;
            }
            const response = await axios.get(url, {
                headers: {
                    Authorization: authHeader,
                    "X-token": authToken,
                    "X-user-id": localStorage.getItem("userId"),
                },
            });
            return response.data; // Array of photos for all stands in fuar
        } catch (error) {
            console.error("Fuar photos alınırken hata oluştu:", error);
            return [];
        }
    }, [authHeader, authToken]);

    const fetchReportsByFuar = useCallback(async (fuarId, salonId = null) => {
        try {
            let url = `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/reports-by-fuar/${fuarId}`;
            if (salonId) {
                url += `?salon_id=${salonId}`;
            }
            const response = await axios.get(url, {
                headers: {
                    Authorization: authHeader,
                    "X-token": authToken,
                    "X-user-id": localStorage.getItem("userId"),
                },
            });
            return response.data; // Array of reports for all stands in fuar
        } catch (error) {
            console.error("Fuar reports alınırken hata oluştu:", error);
            return [];
        }
    }, [authHeader, authToken]);

    const fetchStageColorsByFuar = useCallback(async (fuarId, salonId = null) => {
        try {
            let url = `${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/stage-colors-by-fuar/${fuarId}`;
            if (salonId) {
                url += `?salon_id=${salonId}`;
            }
            const response = await axios.get(url, {
                headers: {
                    Authorization: authHeader,
                    "X-token": authToken,
                    "X-user-id": localStorage.getItem("userId"),
                },
            });
            return response.data; // Array of stage colors for all stands in fuar
        } catch (error) {
            console.error("Fuar stage colors alınırken hata oluştu:", error);
            return [];
        }
    }, [authHeader, authToken]);

    const updatePhotoCategory = async (photoId, categoryId) => {
        try {
            await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/update-photo-category`, {
                photoId,
                categoryId,
            });
        } catch (error) {
            console.error("Fotoğraf kategorisi güncellenirken hata oluştu:", error);
        }
    };

    const handleRemoveCategory = async (photoId) => {
        try {
            await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/remove-photo-category`, {
                photoId,
            });

        } catch (error) {
            console.error("Fotoğraf kaldırılırken hata oluştu:", error);
            alert(t("betterStandsContext.fotografKaldirilirkenHataOlustu"));
        }
    };

    const handleRemovePhotoo = async (photoId, isUncategorized = false) => {
        try {
            // Eğer görsel eşlenmemişse (uncategorized), stand_id null yapılır
            if (isUncategorized) {
                await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/remove-photo-stand`, { photoId });
            } else {
                // Kategorideyse, sadece kategoriden kaldırılır
                await axios.post(`${NODE_BACKEND_API_URL}:${NODE_BACKEND_API_PORT}/api/betterstands/remove-photo-category`, { photoId });
            }
        } catch (error) {
            console.error("Fotoğraf kaldırılırken hata oluştu:", error);
            alert(t("betterStandsContext.fotografKaldirilirkenHataOlustu"));
        }
    };

    const downloadPhotos = async (salonId, fairId, onProgress) => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        if (!token || !userId) {
            throw new Error('Authentication token or user ID not found');
        }

        const MAX_PARTS = 25;
        let currentPart = 1;
        let totalParts = 1;
        let hasPhotos = false;

        console.log('Starting download for salon:', salonId, 'fair:', fairId);

        // Initial progress update
        onProgress?.(0, currentPart, totalParts);

        try {
            while (currentPart <= MAX_PARTS) {
                try {
                    console.log(`Downloading part ${currentPart} of ${totalParts || '?'}`);

                    const response = await axios.get(
                        `https://e.sakagrup.com:5000/api/betterstands/download-photos-by-salon-jszip/salon/${encodeURIComponent(salonId)}/fuar/${fairId}?part=${currentPart}&totalParts=${MAX_PARTS}`,
                        {
                            responseType: 'blob',
                            headers: {
                                'X-token': token,
                                'X-user-id': userId,
                                'Content-Type': 'application/json'
                            },
                            validateStatus: (status) => status < 500, // Don't throw on 404
                            onDownloadProgress: (progressEvent) => {
                                if (progressEvent.lengthComputable) {
                                    const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                    // Only update progress for the current part
                                    onProgress?.(percentComplete, currentPart, totalParts);
                                }
                            }
                        }
                    );

                    // If 404 on first part, no photos exist
                    if (response.status === 404 && currentPart === 1) {
                        throw { noPhotos: true, message: 'No photos found in this category' };
                    }

                    // If 404 on subsequent parts, we're done
                    if (response.status === 404) {
                        console.log('Reached the end of available parts');
                        break;
                    }

                    // If we get here, we have a valid response
                    hasPhotos = true;

                    // Process the download
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `photos_part_${currentPart}.zip`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    // Move to next part
                    currentPart++;

                    // Reset progress for the next part
                    onProgress?.(0, currentPart, totalParts);

                } catch (error) {
                    if (error.noPhotos) {
                        onProgress?.(100, 1, 1);
                        throw new Error('No photos found in this category');
                    }
                    if (error.response?.status === 404 && currentPart > 1) {
                        console.log('Reached the end of available parts');
                        break;
                    }
                    console.error('Error downloading part:', error);
                    onProgress?.(0, 1, 1);
                    throw error;
                }
            }

            // Final progress update
            onProgress?.(100, currentPart - 1, totalParts);

            // If we've gone through all parts and found no photos
            if (!hasPhotos && currentPart === 1) {
                throw new Error('No photos found in this category');
            }

        } catch (error) {
            console.error('Error in download process:', error);
            onProgress?.(0, 1, 1);

            if (error.message.includes('No photos found')) {
                throw new Error(error.message);
            }

            throw new Error('An error occurred while downloading photos. Please try again.');
        }
    };





    return (
        <BetterStandsContext.Provider value={{
            stands,
            salons,
            activeFairId,
            fetchActiveStands,
            getPostHeader,
            fetchAllUncategorizedPhotos,
            fetchSalons,
            assignPhotoToStand,
            saveReport,
            fetchReport,
            uploadPhoto,
            fetchUncategorizedPhotos,
            filteredPhotos,
            loading,
            fetchStands,
            totalPages,
            fetchCategoryPhotos,
            updatePhotoCategory,
            handleRemoveCategory,
            handleRemovePhotoo,
            saveCategoryNote,
            uploadAreaPhoto,
            uncategorizedPhotos,
            fetchCategoryNotes,
            downloadPhotos,
            fetchActiveFairId,
            fuarZamani,
            setFuarZamani,
            detailedStands,
            isLoadingDetailed,
            detailedError,
            fetchDetailedReport,
            fetchPhotosByFuar,
            fetchReportsByFuar,
            fetchStageColorsByFuar,
            updateFairStage

        }}>
            {children}
        </BetterStandsContext.Provider>
    );
};

export default BetterStandsContext;
